// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePropertyButton } from '@/components/editable-property-button';
import { LinkifiedText } from '@/components/linkified-text';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { usePopoverAutoFocus } from '@/hooks/use-popover-auto-focus';
import { useTimeout } from '@/hooks/use-timeout';
import { NotesCopyButton } from '@/order/components/notes-copy-button';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const EMPTY_STATE = 'Click to add notes...';

const formSchema = z.object({ orderNotes: z.string().nullable() });

type FormData = z.infer<typeof formSchema>;

interface OrderNotesControlProps {
  orderNotes: string | null;
  updateNotes: (orderNotes: string | null) => Promise<unknown>;
  onSuccess?: () => void;
  isDisabled?: boolean;
  tooltipTitle?: string;
}

export function OrderNotesControl(props: OrderNotesControlProps) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = usePopoverAutoFocus(editor.isOpen);

  const { formState, register, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { orderNotes: props.orderNotes ?? '' },
  });

  // Reset form with latest data from props
  const resetFormWithLatestData = () => {
    reset({ orderNotes: props.orderNotes ?? '' });
  };

  const updateMutation = useMutation({
    mutationFn: (orderNotes: string | null) => props.updateNotes(orderNotes),
    onSuccess: () => {
      props.onSuccess?.();
      // Reset form with latest data before closing
      resetFormWithLatestData();
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const { isDirty } = formState;

  const handleOpen = () => {
    // Reset form with latest data when opening editor
    resetFormWithLatestData();
    editor.open();
  };

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (isDirty) {
      // Transform empty string to null before sending to API
      const orderNotes =
        data.orderNotes?.trim() === '' ? null : data.orderNotes;
      return updateMutation.mutate(orderNotes);
    }

    editor.close();
  };

  const notes = props.orderNotes ?? EMPTY_STATE;
  const color = props.orderNotes === null ? 'text.secondary' : 'text.primary';
  const isDisabled = props.isDisabled ?? false;
  const hasNotes = props.orderNotes !== null && props.orderNotes.trim() !== '';

  return (
    <>
      <Tooltip title={props.tooltipTitle ?? ''} arrow>
        <span style={{ display: 'block', height: '100%' }}>
          <Box
            component="span"
            sx={{ display: 'block', height: '100%', position: 'relative' }}
          >
            <EditablePropertyButton
              ref={buttonRef}
              component="div"
              onClick={isDisabled ? undefined : handleOpen}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                alignItems: 'start',
                justifyContent: 'start',
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                borderLeft: '2px solid',
                borderColor: 'divider',
                borderRadius: 0,
                marginLeft: 0,
                position: 'relative',
                overflow: 'auto',
                cursor: isDisabled ? 'default' : 'pointer',
                '& .edit-icon': {
                  position: 'absolute',
                  top: '0.4rem',
                  right: hasNotes ? '2.3rem' : '0.3rem',
                  margin: 0,
                  opacity: isDisabled ? 0.3 : 1,
                },
              }}
            >
              <Box component="span" sx={{ paddingRight: hasNotes ? 6 : 0 }}>
                <Typography
                  component="span"
                  variant="body2"
                  textAlign="left"
                  marginRight="auto"
                  whiteSpace="pre-wrap"
                  color={color}
                >
                  <LinkifiedText>{notes}</LinkifiedText>
                </Typography>
              </Box>
            </EditablePropertyButton>
            {hasNotes && props.orderNotes !== null && (
              <NotesCopyButton notes={props.orderNotes} />
            )}
          </Box>
        </span>
      </Tooltip>
      <SupportSnackbar
        isOpen={updateMutation.isError}
        onClose={updateMutation.reset}
        message="An error occurred while updating the notes."
      />
      <PopoverForm
        verticalOffset={8}
        position="left"
        verticalPosition="bottom"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          multiline
          rows={5}
          size="small"
          InputProps={register('orderNotes')}
          sx={{ width: '20rem' }}
        />
      </PopoverForm>
    </>
  );
}
